import EditIcon from "@mui/icons-material/Edit";
import { CircularProgress } from "@mui/material";
import { type FC, useCallback, useEffect, useState } from "react";
import { useUpdateShiftNotes } from "../../../../hooks/useUpdateShiftNotes";
import type { NurseInShift } from "../../../../utils/models";
import { SystemValidationTextField } from "../../../UIToolkit/Inputs/SystemValidationTextField";

type Props = {
	nurseInShift: NurseInShift;
	onUpdateComplete?: () => void;
	isParentHovered?: boolean;
};

/**
 * Component to edit notes for a nurse in shift
 * behavior is: click -> edit notes -> on blur or enter save notes
 * @param nurseInShift
 * @param onUpdateComplete
 * @param isParentHovered
 */
export const NurseInShiftNotesInput: FC<Props> = ({ nurseInShift, onUpdateComplete, isParentHovered }) => {
	const { saveNotes, isSaving } = useUpdateShiftNotes(nurseInShift, false);
	const [nurseInShiftsNotes, setNurseInShiftsNotes] = useState(nurseInShift.notes ?? "");

	const handleNotesUpdate = useCallback(async () => {
		await saveNotes(nurseInShiftsNotes);
		onUpdateComplete?.();
	}, [saveNotes, nurseInShiftsNotes, onUpdateComplete]);

	useEffect(() => {
		// 	we need to handle pressing enter to save notes
		const handleKeyDown = async (event: KeyboardEvent) => {
			if (event.key === "Enter") {
				event.preventDefault();
				await handleNotesUpdate();
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [handleNotesUpdate]);

	return (
		<SystemValidationTextField
			autoFocus={true}
			variant={"standard"}
			reserveHeightForHelperText={false}
			dataTestId="nurse-in-shift-notes-input"
			inputProps={{
				onBlur: handleNotesUpdate,
				endAdornment: isSaving ? (
					<CircularProgress size={"12px"} />
				) : (
					<EditIcon
						className={"edit-icon"}
						fontSize={"inherit"}
						sx={{
							opacity: isParentHovered ? 1 : 0,
							mb: "4px",
						}}
					/>
				),
				sx: {
					color: "inherit",
					fontSize: "inherit",
				},
			}}
			value={nurseInShiftsNotes}
			onChange={(event) => {
				setNurseInShiftsNotes(event.target.value);
			}}
		/>
	);
};
